import axios from "axios"; // tslint:disable-line match-default-export-name
import { makeObservable, observable } from "mobx";

import { gGateway } from "@utils/gateway";

import { nonConcurrent } from "@use/decorators";
import { EntityStore } from "@use/entity-store";
import { Collection } from "@use/models";

import { urls } from "@courses-models/course/src/ts/config";
import {
  CourseData,
  CourseModel,
  CreateCourseData,
} from "@courses-models/course/src/ts/models/CourseModel";
import { asyncTimeout } from "@use/async-timeout";

/**
 * Memory cache of collections that have already been fetched by the client, so
 * that we don't need to fetch the same collections over and over again.
 */
const cache = new EntityStore<CourseCollection>({ node: false, ttl: 600 });

/**
 * Specification of the response format when fetching lists of courses from the
 * remote backend.
 */
type CourseListResponse = {
  objectID: string;
  created: number;
  modified: number;
  userId: string;
  title: string;
  subjects: string[];
  levels: string[];
  sharedWith?: string[];
  clonedTo?: string[];
  clonedFrom?: string;
  courseElements: {
    id: string;
    title: string;
    type: string; // tslint:disable-line no-reserved-keywords
    note: {
      isVisible: boolean;
      content: string;
    };
    content: string;
  }[];
}[];

/**
 * Specification of the response format when fetching the list of courses
 * shared with students.
 */
type CourseShareResponse = {
  created: number;
  modified: number;
  sharedObjectID: string;
  sharedByName: string;
  title: string;
  subjects: string[];
  levels: string[];
}[];

/**
 * Iterable collection of courses, which will automatically update when one of
 * the contained courses are updated.
 */
export class CourseCollection extends Collection<CourseModel> {
  /**
   * Contains an internal set of courses which are contained within this
   * collection of courses.
   */
  protected readonly collection: CourseModel[];

  /**
   * Create an internal set of courses, and subscribe to delete events, so
   * that our collection is always kept up-to-date.
   */
  constructor(courses: (CourseData | CourseModel)[]) {
    super();

    // Convert list of courses to an internal set
    this.collection = courses.map((course) =>
      course instanceof CourseModel ? course : new CourseModel(course),
    );

    makeObservable(this as unknown as { collection: CourseModel[] }, {
      collection: observable,
    });
  }

  /**
   * Fetches a collection of courses that was shared with this student, and
   * converts each course into a well-typed model.
   */
  @nonConcurrent()
  public static async GET_BY_STUDENT_ID(id: string): Promise<CourseCollection> {
    const cacheId = `student/${id}`;

    // If we've already cached a collection of courses for this student,
    // then return it "as is"
    if (cache.has(cacheId)) {
      return <CourseCollection>cache.get(cacheId);
    }

    // Fetch the list of courses that have been shared with this user
    const response = await axios.get<CourseShareResponse>(
      gGateway(
        `${urls.userData}/Shares/Courses?userID=${encodeURIComponent(
          id,
        )}&version=2`,
      ),
    );
    const data = response.data;

    // Create a new collection instance
    const collection = new CourseCollection(
      data.map((course) => ({
        id: course.sharedObjectID,
        createdAt: course.created,
        modifiedAt: course.modified,
        userId: id,
        title: course.title,
        subjects: course.subjects,
        levels: course.levels,
      })),
    );

    // Make sure this collection is available for use later on
    cache.set(cacheId, collection);

    return collection;
  }

  /**
   * Fetches a collection of courses that the given teacher has access to (ie.
   * both courses the teacher created and courses shared with him), and
   * converts each course into a well-typed model.
   */
  @nonConcurrent()
  public static async GET_BY_TEACHER_ID(id: string): Promise<CourseCollection> {
    const cacheId = `teacher/${id}`;

    // If we've already cached a collection of courses for this teacher,
    // then return it "as is"
    if (cache.has(cacheId)) {
      return <CourseCollection>cache.get(cacheId);
    }

    // Fetch the list of courses that have been shared with this user
    const response = await axios.get<CourseListResponse>(
      gGateway(`${urls.userData}/v2/Courses?userID=${encodeURIComponent(id)}`),
    );
    const data = response.data;

    // Create a new collection instance
    const collection = new CourseCollection(
      data.map((course) => ({
        id: course.objectID,
        createdAt: course.created,
        modifiedAt: course.modified,
        userId: id,
        title: course.title,
        subjects: course.subjects,
        levels: course.levels,
        shared: course.clonedFrom
          ? {
              sharedBy: "",
              clonedFrom: course.clonedFrom,
            }
          : undefined,
      })),
    );

    // Make sure this collection is available for use later on
    cache.set(cacheId, collection);

    return collection;
  }

  /**
   * Helper that'll perform a request to create a course with the specified
   * data.
   */
  public async create(
    data: CreateCourseData,
    minDelay: number = 0,
  ): Promise<CourseModel> {
    // Start by running the request to create the course...
    return this.runCreateRequest(
      async () => CourseModel.CREATE(data),
      minDelay,
    );
  }

  /**
   * Helper that'll create a copy of the specified course.
   */
  public async duplicate(
    data: { course: CourseModel; titlePrefix: string },
    minDelay: number = 0,
  ): Promise<CourseModel> {
    // Register when the request was started, so we can enforce a minimum
    // delay later on
    const startedAt = Date.now();

    // Perform request to duplicate the request at the backend
    const response = await axios.post<string>(
      gGateway(
        `${urls.userData}/v2/Courses/${encodeURIComponent(
          data.course.id,
        )}/Duplicate`,
      ),
    );

    // Wait the specified minimum amount of time
    await asyncTimeout(Math.max(0, minDelay - (Date.now() - startedAt)));

    // Now push the newly created course to the collection
    const course = new CourseModel({
      id: response.data,
      createdAt: Math.round(Date.now() / 1000),
      modifiedAt: Math.round(Date.now() / 1000),
      userId: data.course.userId,
      title: `${data.titlePrefix} ${data.course.title}`,
      subjects: data.course.subjects,
      levels: data.course.levels,
    });

    this.add(course);

    // Return the created course publicly
    return course;
  }

  /**
   * Helper that'll perform a request to delete the specified course from the
   * user's collection.
   */
  // tslint:disable-next-line no-reserved-keywords
  public async delete(
    course: CourseModel,
    minDelay: number = 0,
  ): Promise<void> {
    return this.runDeleteRequest(
      async () => CourseModel.DELETE(course),
      minDelay,
    );
  }

  /**
   * Helper that'll delete the course with the specified id from the user's
   * collection.
   */
  public async deleteById(id: string): Promise<void> {
    const course = this.getById(id);

    // Bail out if course wasn't found
    if (!course) {
      if (process.env.NODE_ENV !== "production") {
        console.warn("CourseCollection.deleteById(...): Course wasn't found.");
      }

      return;
    }

    return this.delete(course);
  }
}
